import { 
  ReportCertificationRenewalRoute,
  ReportCertificationStatusRoute,
  ReportManagementSummaryRoute,
  ReportMISRoute,
  ReportSelectedNotTestedRoute,
  ReportSummaryRoute,
} from "../../config/Routes"
import GridResponse from "../GridResponse"
import { ObservableFromFetch } from "../ObservableFromFetch"

export enum WorkforceReportType {
  CertificationRenewal = 42,
  CertificationStatus = 41,
  ManagementSummary = 2,
  MIS = -37,
  SelectedNotTested = 6,
  Summary = -45,
}

const Reports = [
  ReportCertificationRenewalRoute,
  ReportCertificationStatusRoute,
  ReportManagementSummaryRoute,
  ReportMISRoute,
  ReportSelectedNotTestedRoute,
  ReportSummaryRoute,
] as const
export type Report = typeof Reports[number]
export type WorkforceReportRecord = {
  type: WorkforceReportType
  title: string
}
export type WorkforceReport = Record<Report, WorkforceReportRecord>

export default function getWorkforceReport(report: Report) {
  const WorkforceReports: WorkforceReport = {
    [ReportCertificationRenewalRoute]: { type: WorkforceReportType.CertificationRenewal, title: "Driver Certification Renewal Report" },
    [ReportCertificationStatusRoute]: { type: WorkforceReportType.CertificationStatus, title: "Driver Certification Status Report" },
    [ReportManagementSummaryRoute]: { type: WorkforceReportType.ManagementSummary, title: "Management Summary Report" },
    [ReportMISRoute]: { type: WorkforceReportType.MIS, title: "MIS Report" },
    [ReportSelectedNotTestedRoute]: { type: WorkforceReportType.SelectedNotTested, title: "Selected Not Tested Report" },
    [ReportSummaryRoute]: { type: WorkforceReportType.Summary, title: "Summary Report" },
  }
  return WorkforceReports[report]
}

export function RegulatoryModesGet() {
  return ObservableFromFetch<string[]>("report/GetRegulatoryList")
}

export function YearListGet() {
  return ObservableFromFetch<number[]>("report/GetYearList")
}

export interface ManagementSummaryReport {
  AccountTypeID: number
  BreakByCompanies: boolean
  BreakByLocations: boolean
  ClientDetails: object
  Company: number[]
  DateFrom: string
  DateTo: string
  DateType: string
  IncludeAlcoholTests: boolean
  MasterAccount?: number | null
  ReportID: number
}

export interface MISReport {
  AccountType: number
  AccountID: number
  DtFromDate: string
  DtToDate: string
  RegMode: string
  ReportMode: number
  Year: string
  Company:number[]
}

export interface ExcusedReport {
  AccountID: number
  PoolID: number
  PoolName: string
}

export interface AuditPoolReport {
  DateFrom: string
  DateTo: string
  AllDates: boolean
  PoolID: number
  PoolName: string
}

export interface PositiveReport {
  DateFrom: string
  DateTo: string
  DateType: string
  CompanyIds: number[]
  PageNumber?: number
  PageSize?: number
}

export interface PositiveReportRow {
  CompanyName: string
  LocationName: string
  TestDetailID: string
  TestEventID: string
  RegulatoryMode: string
  EmployeeCategory: string
  ReferenceID: string
  DonorID: string
  DonorFirstName: string
  DonorLastName: string
  TestType: string
  ResultCode: string
  ResultDescription: string
  DateCollected: string
  MROReportDate: string
  PositiveDrugList: string
  Reason: string
  Regulated: string
}

export function ManagementSummaryReportGenerate(parameters: ManagementSummaryReport) {
  const requestBody = JSON.stringify(parameters)
  return ObservableFromFetch<string>("WorkforceReport/ManagementSummaryReport", requestBody)
}

export function MISReportGenerate(parameters: MISReport) {
  const requestBody = JSON.stringify(parameters)
  return ObservableFromFetch<string>("report/MISReport", requestBody)
}

export function ExcusedReportGenerate(parameters: ExcusedReport) {
  const requestBody = JSON.stringify(parameters)
  return ObservableFromFetch<string>("WorkforceReport/ExcusedReport", requestBody)
}

export function AuditPoolReportGenerate(parameters: AuditPoolReport) {
  const requestBody = JSON.stringify(parameters)
  return ObservableFromFetch<string>("WorkforceReport/AuditPoolReport", requestBody)
}

export interface SelectedNotTestedReportParameters {
  fromDate?: string
  toDate?: string
  accountID?: number
  poolID?: number
  personID?: number
  CompanyName: string
  LocationName: string
  PoolName: string
  DonorName: string
  OutputFormat:string
}

export function SelectedNotTestedReportGenerate(parameters: SelectedNotTestedReportParameters) {
  const requestBody: string = JSON.stringify(parameters)

  return ObservableFromFetch<string>("WorkforceReport/SelectedNotTestedReport", requestBody)
}

export function PositiveReportLoad(parameters: PositiveReport) {
  const requestBody = JSON.stringify(parameters)
  return ObservableFromFetch<GridResponse<PositiveReportRow>>("WorkforceReport/PositiveReport", requestBody)
}

export function PositiveReportExport(parameters: PositiveReport) {
  const requestBody = JSON.stringify(parameters)
  return ObservableFromFetch<string>("WorkforceReport/ExportPositiveReport", requestBody)
}
